<template>
  <div>
    <toolbar :title="`${currentCompany.companyName} : Placed Bids`">
      <v-icon @click="loadBids()">refresh</v-icon>
    </toolbar>

    <v-layout column>
      <v-layout class="xs12">
        <v-layout v-if="isMobile" column class="xs12">
          <v-layout row v-if="!bids.length">
            <v-flex>
              <v-alert :value="true" color="primary" type="info">
                No bids yet
              </v-alert>
            </v-flex>
          </v-layout>
          <v-card v-for="bid in bids" :key="bid.id" class="xs12 my-2">
            <v-tabs>
              <v-tab ripple> Details </v-tab>
              <v-tab ripple> Modify </v-tab>
              <v-tab ripple> Map </v-tab>
              <v-tab ripple> Conversation </v-tab>
              <v-tab-item :key="1">
                <v-card-title>
                  <v-layout column>
                    <v-flex>
                      <b>From:</b>
                      <span>{{bid.listing | originName}}</span><br>
                      <b>To:</b>
                      <span>{{bid.listing | destinationName}}</span>
                    </v-flex>

                    <v-flex>
                      <p class="mb-0">${{bid.amount}}
                        <span class="font-italic font-weight-light">
                          (${{getPerDistanceCost(bid)}}/{{ siteDistanceMeasureUnitLabel }})
                        </span>
                      </p>
                    </v-flex>

                  </v-layout>
                </v-card-title>

                <v-card-text class="pt-0">
                  <b>Loads:</b> {{ bid.numberOfLoads }}<br>
                  <b>Pickup Time:</b> {{bid.pickupTime | formatDate}}<br/>
                  <b>Placed:</b> {{bid.modified | formatDate}}<br/>

                  <v-layout class="pt-2">
                    <v-flex justify-center>
                      <v-chip :color="getChipColor(bid.status)" text-color="white">
                        {{convertBidStatusToText(bid.status)}}
                      </v-chip>
                    </v-flex>
                    <v-flex justify-center>
                      <v-chip v-if="hasW9(bid)"
                              color="green" text-color="white">
                        {{$t('company-profile.vendor-tax-document')}}
                        <v-icon right>check</v-icon>
                      </v-chip>

                      <v-chip v-else-if="canAcceptBid(bid.listing)"
                              color="orange" text-color="white">
                        Missing {{$t('company-profile.vendor-tax-document')}}
                        <v-icon right class="ml-1">warning</v-icon>
                      </v-chip>
                    </v-flex>
                  </v-layout>
                </v-card-text>

              </v-tab-item>
              <v-tab-item>
                <v-card-title primary-title class="pb-2 pt-2">
                  <h4>Listing Company:&nbsp;</h4>
                  <span>{{bid.listing.listingCompany.name}}</span>
                </v-card-title>

                <v-container>
                  <bid-form @refresh="$emit('refresh')"
                            hideMapAndConversation
                            :hide-form="hideForm"
                            :disable-form="disableForm"
                            :bid="bid">
                  </bid-form>
                </v-container>
              </v-tab-item>
              <v-tab-item class="pa-3" lazy>
                <load-map
                  :disable-zoom="!signedIn"
                  :origin-place-id="_.get(bid, 'listing.pickupLocation.googlePlace.placeId', null)"
                  :destination-place-id="_.get(bid, 'listing.dropoffLocation.googlePlace.placeId', null)" />
              </v-tab-item>
              <v-tab-item class="pa-3">
                <bid-conversation :bid-id="bid.id" />
              </v-tab-item>
            </v-tabs>
          </v-card>
        </v-layout>

        <v-layout column v-else>
          <v-card>
            <v-master-detail-pane>
              <template slot="master">
                <v-load-nav-list title="Bids"
                                 searchPrompt="Search"
                                 :loading="loadingBids"
                                 :accurateSince="accurateSince"
                                 :loads="bids"
                                 :company="currentCompany"
                                 @update:searchTerm="search = $event"
                                 @refresh="loadBids(currentCompany.id)">

                  <template slot="loadTile"
                            slot-scope="{ idx, load: bid }">

                    <v-list-tile :key="`bid_${idx}_${bid.id}`"
                                 @click="select(bid)"
                                 :class="{'primary lighten-1': isSelected(bid)}"
                                 :inactive="isSelected(bid)"
                                 ripple avatar>

                      <v-list-tile-content>
                        <v-list-tile-title class="text--primary">
                          ${{bid.amount}}
                          <span class="font-italic font-weight-light">
                          (${{getPerDistanceCost(bid)}}/{{ siteDistanceMeasureUnitLabel }})
                        </span>
                          <span class="font-italic font-weight-light">
                          {{convertBidStatusToText(bid.status)}}
                        </span>
                        </v-list-tile-title>
                        <v-list-tile-sub-title>
                          <b>From:</b> <span>{{bid.listing | originName}}</span> <br>
                          <b>To:</b> <span>{{bid.listing | destinationName}}</span>
                        </v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>
                    <v-divider :key="`bid_divider_${idx}_${bid.id}`"/>
                  </template>
                </v-load-nav-list>
              </template>
              <template slot="detail">
                <bid-details v-if="selected"
                             :bid="selected">
                </bid-details>
              </template>
            </v-master-detail-pane>
          </v-card>
        </v-layout>
      </v-layout>
    </v-layout>
  </div>
</template>

<script>
import _ from 'lodash';
import API from '@/api';
import moment from 'moment';
import companyNamesMixin from '@/mixins/companyNames';
import BidConversation from '@/components/BidConversation';
import LoadMap from '@/components/LoadMap';
import BidForm from '@/components/BidForm';
import Toolbar from '@/components/Toolbar';
import VMasterDetailPane from '@/components/MasterDetailPane';
import BidDetails from '@/components/BidDetails';
import VLoadNavList from '@/components/dashboard/simple/LoadNavList';
import companySectionMixin from '@/mixins/companySectionMixin';

import { bidStatus } from '@/types';
import { mapGetters } from 'vuex';
import {
  accountHasOfficePermission,
} from '@/store/permissions';
import pluginMixin from '@/mixins/pluginMixin';
import unitsOfMeasurePlugin from '@/mixins/unitsOfMeasurePlugin';

export default {
  name: 'PlacedBids',
  mixins: [
    companySectionMixin,
    companyNamesMixin,
    pluginMixin,
    unitsOfMeasurePlugin,
  ],
  props: {
    hideForm: {
      type: Boolean,
      default() {
        return false;
      },
    },
    disableForm: {
      type: Boolean,
      default() {
        return false;
      },
    },
    listing: {
      type: Object,
      default() {
        return null;
      },
    },
    showMap: {
      type: Boolean,
      default() {
        return false;
      },
    },
    updateDate: {
      type: Boolean,
      default() {
        return false;
      },
    },
    expand: {
      type: Boolean,
      default() {
        return false;
      },
    },
    searchBidId: {
      type: String,
    },
  },
  components: {
    BidDetails,
    VMasterDetailPane,
    BidConversation,
    VLoadNavList,
    LoadMap,
    BidForm,
    Toolbar,
  },
  filters: {
    formatTimeStamp(momentDate) {
      return momentDate.format('HH:mm A MMM DD');
    },
  },
  data() {
    return {
      selected: null,
      headers: [
        { text: '', sortable: false, class: 'id-col-header' },
        { text: 'Status', sortable: true, value: 'status' },
        { text: 'Load Route', sortable: false },
        { text: 'Bid Amount', sortable: true, value: 'bid-amount' },
        { text: 'Loads', sortable: false, value: 'loads' },
        { text: 'Pickup Time', sortable: true, value: 'pickup-time' },
        { text: 'Placed', sortable: true, value: 'placed-time' },
        { text: ' ', sortable: false, value: 'icons' },
      ],
      bidToUpdate: {},
      bidModifyModal: false,
      bidStatuses: [
        { text: 'Active', value: bidStatus.ACTIVE },
        { text: 'Retracted', value: bidStatus.CANCELLED_BY_BIDDER },
        { text: 'Accepted', value: bidStatus.ACCEPTED },
      ],
      loadingBtn: false,
      minDatetime: new Date().toISOString(),
      bidStatus,
    };
  },
  watch: {
    companyId() {
      this.loadBids();
    },
  },
  computed: {
    _() {
      return _;
    },
    ...mapGetters('companies', [
      'currentCompany',
    ]),
    ...mapGetters('profiles', [
      'isLtnAdmin',
    ]),
    ...mapGetters('bids', {
      bids: 'bids',
      bidsTimestamp: 'timestamp',
      loadingBids: 'loadingBids',
    }),
    companyId() {
      return this.currentCompany.id;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    accurateSince() {
      if (this.loadingBids) {
        return 'Loading ...';
      }

      if (this.bidsTimestamp) {
        return `Updated ${this.$options.filters.formatTimeStamp(this.bidsTimestamp)}`;
      }

      return 'Refresh';
    },
  },
  methods: {
    ...mapGetters('profiles', [
      'findCompanyAccount',
    ]),
    select(bid) {
      this.selected = bid;
    },
    loadBid() {
      // TODO
    },
    isSelected(bid) {
      return _.get(this.selected, 'id', null) === bid.id;
    },
    onRefresh() {
      this.loadCompanyTransactions(this.company.id);
    },
    getPerDistanceCost(bid) {
      return `${this.distanceRate(bid.amount, bid.listing.loadedMiles)}`;
    },
    canAcceptBid(listing) {
      return this.hasOfficePermission(listing) || this.isLtnAdmin;
    },
    sortBids(items, index, isDescending) {
      let sortedItems;
      switch (index) {
        case 'pickup-time':
          sortedItems = _.sortBy(items, [bid =>
            moment(bid.pickupTime, moment.ISO_8601).unix()]);
          break;
        case 'placed-time':
          sortedItems = _.sortBy(items, [bid =>
            moment(bid.modified, moment.ISO_8601).unix()]);
          break;
        default:
          sortedItems = _.sortBy(items, [bid => bid[index]]);
      }

      if (isDescending) {
        sortedItems = sortedItems.reverse();
      }

      return sortedItems;
    },
    isTCVerified(bid) {
      return !_.isEmpty(_.get(bid, 'truckingCompany.verified', null));
    },
    hasW9(bid) {
      return !_.isEmpty(_.get(bid, 'truckingCompany.w9Url', null));
    },
    hasIP(bid) {
      return !_.isEmpty(_.get(bid, 'truckingCompany.insurancePolicyUrl', null));
    },
    loadData() {
      this.loadBids();
    },
    getChipColor(status) {
      switch (status) {
        case bidStatus.ACTIVE: {
          return 'success';
        }
        case bidStatus.ACCEPTED: {
          return 'primary';
        }
        case bidStatus.CANCELLED_BY_BIDDER: {
          return '';
        }
        default:
      }
      return '';
    },
    convertBidStatusToText(status) {
      const convertBidStatus = this.bidStatuses.find(b => b.value === status);
      if (!convertBidStatus) {
        return status;
      }
      return convertBidStatus.text;
    },
    hasOfficePermission(listing) {
      const companyId = _.get(listing, 'listingCompany.id', null);
      return accountHasOfficePermission(this.findCompanyAccount()(companyId));
    },
    async cancelBid(bid) {
      this.loadingBtn = true;
      try {
        await API.bid.deleteBid({ bidId: bid.id });

        this.$store.dispatch('applications/setSuccessNotification', {
          message: 'Bid Canceled',
        });

        this.$emit('refresh');
      } catch (error) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: 'An error occurred, please try again later',
        });
      } finally {
        this.loadingBtn = false;
      }
    },
    loadBids(companyId) {
      this.$store.dispatch('bids/getBids', companyId || this.companyId);
    },
  },
  created() {
    this.loadBids();
  },
};
</script>

<style>
  .id-col-header {
    padding-left: 35px !important;
  }

  .id-col {
    position: relative;
    padding-left: 35px !important;
  }

  .expand-icon-details {
    position: absolute;
    left: 5px;
    top: 10px;;
    bottom: auto;
    margin-top: 10px;
  }

</style>



// WEBPACK FOOTER //
// src/pages/Companies/PlacedBids.vue